/**
 * PROTOTYPE. Floating variant switcher for the Diagnostics redesign.
 * Dev builds only: the gate is `import.meta.env.DEV`, so a stray merge cannot
 * ship the bar. Arrow keys cycle unless a text field has focus.
 */
import { useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { cn } from "../../../lib/utils";

export const PROTOTYPE_VARIANTS = ["A", "B", "C", "D"] as const;
export type PrototypeVariant = (typeof PROTOTYPE_VARIANTS)[number];

const PROTOTYPE_VARIANT_NAMES: Readonly<Record<PrototypeVariant, string>> = {
  A: "Verdict, then facts",
  B: "Rail and ledger",
  C: "Status strip and timeline",
  D: "Upstream stat strips",
};

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
}

export function PrototypeSwitcher({
  current,
  onChange,
}: {
  readonly current: PrototypeVariant;
  readonly onChange: (variant: PrototypeVariant) => void;
}) {
  const index = PROTOTYPE_VARIANTS.indexOf(current);
  const previous =
    PROTOTYPE_VARIANTS[(index + PROTOTYPE_VARIANTS.length - 1) % PROTOTYPE_VARIANTS.length]!;
  const next = PROTOTYPE_VARIANTS[(index + 1) % PROTOTYPE_VARIANTS.length]!;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (event.key === "ArrowLeft") onChange(previous);
      if (event.key === "ArrowRight") onChange(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, onChange, previous]);

  if (!import.meta.env.DEV) return null;

  return (
    <div
      role="group"
      aria-label="Prototype variant"
      className={cn(
        "fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-1 rounded-full",
        "border border-border bg-popover px-1.5 py-1 text-xs text-popover-foreground shadow-lg",
      )}
    >
      <button
        type="button"
        aria-label="Previous variant"
        className="inline-flex size-7 items-center justify-center rounded-full hover:bg-accent"
        onClick={() => onChange(previous)}
      >
        <ChevronLeftIcon className="size-4" aria-hidden="true" />
      </button>
      <span className="px-2 font-medium">
        {current}{" "}
        <span className="text-muted-foreground">({PROTOTYPE_VARIANT_NAMES[current]})</span>
      </span>
      <button
        type="button"
        aria-label="Next variant"
        className="inline-flex size-7 items-center justify-center rounded-full hover:bg-accent"
        onClick={() => onChange(next)}
      >
        <ChevronRightIcon className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
